import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { purchaseBook } from '../api/books';
import { useNavigate } from 'react-router-dom';
import GemIcon from '@/components/icons/GemIcon';
import { analyticsService } from '@/services/analyticsService';

interface PurchaseBookDialogProps {
  isOpen: boolean;
  onClose: () => void;
  book: {
    book_id: number;
    title: string;
    author?: string;
    cover_image_url?: string | null;
    price?: string | number;
  };
}

const PurchaseBookDialog: React.FC<PurchaseBookDialogProps> = ({
  isOpen,
  onClose,
  book,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const purchaseMutation = useMutation({
    mutationFn: () => purchaseBook(book.book_id),
    onSuccess: async (data) => {
      setMessage(data.message);
      setError(null);
      
      // Update book detail cache
      queryClient.setQueryData(['book', book.book_id], (prev: any) => {
        if (!prev) return prev;
        return { ...prev, is_purchased: true };
      });
      
      // Invalidate related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userProfile'] }),
        queryClient.invalidateQueries({ queryKey: ['userLibrary'] }),
        queryClient.invalidateQueries({ queryKey: ['my-sections', book.book_id], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['timeline-events', book.book_id], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['character-analysis', book.book_id], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['character-relationships', book.book_id], refetchType: 'all' }),
      ]);
      
      // Navigate to learning path after 1.5 seconds
      setTimeout(() => {
        onClose();
        setMessage(null);
        navigate(`/learning/path`, { state: { bookId: book.book_id } });
      }, 1500);
    },
    onError: (err: any) => {
      const errorMessage = err?.message || 'خطا در خرید کتاب';
      setError(errorMessage);
      setMessage(null);
    },
  });

  const handlePurchase = () => {
    setMessage(null);
    setError(null);
    void analyticsService.logEvent('book_gem_purchase_click', {
      book_id: String(book.book_id),
      book_title: book.title,
      button_type: 'confirm_purchase',
    });
    purchaseMutation.mutate();
  };

  const handleBuyGems = () => {
    onClose();
    navigate('/gems');
  };

  if (!isOpen) return null;

  const bookPrice = Number(book.price || 0);
  const isInsufficientGems = error?.includes('الماس کافی') || error?.includes('نداری');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        {/* Header */}
        <div className="relative flex items-center justify-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 font-persian">خرید کتاب</h2>
          <button
            onClick={onClose}
            className="absolute left-6 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="بستن"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Book Info */}
          <div className="flex items-start gap-4 mb-6">
            {/* Cover */}
            <div className="flex-shrink-0">
              <img
                src={book.cover_image_url || '/placeholder-book.png'}
                alt={book.title}
                className="w-20 h-28 object-cover rounded-lg shadow-md"
              />
            </div>

            {/* Title and Price */}
            <div className="flex-1">
              <h3 className="text-base font-bold text-gray-900 font-persian mb-3 leading-relaxed">
                {book.title}
              </h3>
              <div className="text-sm text-gray-600 font-persian mb-2">
                <span>نویسنده: </span>
                <span className="font-semibold">{book.author || 'نامشخص'}</span>
              </div>
            </div>
          </div>

          {/* Access Description */}
          <div className="mb-6">
            <p className="text-sm text-gray-900 font-persian text-center leading-relaxed">
              دسترسی همیشگی به قابلیت‌های کتاب {book.title}
            </p>
          </div>

          {/* Message/Error Display */}
          {message && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700 font-persian text-center">{message}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-600 border border-red-700 rounded-lg">
              <p className="text-sm text-white font-persian text-center">{error}</p>
            </div>
          )}

          {/* Purchase Button */}
          {!isInsufficientGems ? (
            <button
              onClick={handlePurchase}
              disabled={purchaseMutation.isPending}
              className={`w-full py-3.5 rounded-xl font-persian font-bold text-sm transition-all flex items-center justify-center gap-1.5 ${
                purchaseMutation.isPending
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-cyan-500 text-white hover:bg-cyan-600 active:scale-[0.98]'
              }`}
            >
              {purchaseMutation.isPending ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  <span>در حال خرید...</span>
                </>
              ) : (
                <>
                  <span>خرید کتاب</span>
                  <span>با</span>
                  <span>{bookPrice.toLocaleString('fa-IR')}</span>
                  <span className="relative -top-0.5">
                    <GemIcon size={20} color="white" />
                  </span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleBuyGems}
              className="w-full py-3.5 rounded-xl font-persian font-bold text-sm bg-cyan-500 text-white hover:bg-cyan-600 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5"
            >
              <GemIcon size={20} color="white" />
              <span>خرید الماس</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseBookDialog;
